import { useCallback } from 'react';
import { SceneWithUserNames } from '../../../../types/scene.types';
import { useUnifiedFieldEdit } from '../../shortcut/useUnifiedFieldEdit';

/**
 * 场景表格单元格编辑相关的 Hook
 * 处理单元格的编辑激活和完成等操作
 * 
 * 主要功能：
 * - activateCellEdit: 激活单元格编辑状态，包括聚焦输入框、打开 InputRecommend 组件、设置下拉菜单状态等
 * - finishCellEdit: 完成单元格编辑，包括移除焦点、提交待提交的更新等
 * 
 * 注意：此 Hook 现在使用统一的 useUnifiedFieldEdit 来管理状态，确保与其他页面的一致性
 */
export const useSceneCellEdit = (
  scenes: SceneWithUserNames[],
  // 场景选择状态
  setSelectedSceneId: React.Dispatch<React.SetStateAction<string | null>>,
  setSelectedSceneData: React.Dispatch<React.SetStateAction<Partial<import('../../../../types/scene.types').SceneInfo> | undefined>>,
  // SetSearch 相关状态
  setActiveSceneId: React.Dispatch<React.SetStateAction<string | null>>,
  setSetSearchVisible: React.Dispatch<React.SetStateAction<boolean>>,
  setSetSearchText: React.Dispatch<React.SetStateAction<string>>,
  setLocalSceneSetDisplay: React.Dispatch<React.SetStateAction<Map<string, string>>>,
  setSetSearchPosition: React.Dispatch<React.SetStateAction<import('../../BreakdownMain/BreakdownMain.types').MenuPosition | null>>,
  // 地点搜索相关状态
  setActiveLocationSceneId: React.Dispatch<React.SetStateAction<string | null>>,
  setSceneLocationSearchVisible: React.Dispatch<React.SetStateAction<boolean>>,
  setSceneLocationSearchText: React.Dispatch<React.SetStateAction<string>>,
  setLocalSceneLocationDisplay: React.Dispatch<React.SetStateAction<Map<string, string>>>,
  setSceneLocationSearchPosition: React.Dispatch<React.SetStateAction<import('../../BreakdownMain/BreakdownMain.types').MenuPosition | null>>,
  sceneLocationDisplayTexts: { [sceneId: string]: string },
  // 立即提交更新函数
  flushSceneUpdate: (sceneId: string) => Promise<void>
) => {
  // 使用统一的字段编辑 Hook
  const { activateFieldEdit, finishFieldEdit } = useUnifiedFieldEdit();

  // 激活单元格编辑状态
  const activateCellEdit = useCallback((rowIndex: number, columnKey: string) => {
    const scene = scenes[rowIndex];
    if (!scene) return;

    // 查找字段元素（只查找表格内的元素，避免找到详情页的元素）
    let fieldElement: HTMLElement | null = null;
    
    // 先查找表格容器
    const tableContainer = document.querySelector('.breakdown-table-container');
    
    if (tableContainer) {
      // 对于内外和日夜下拉框，查找 div 元素（只查找表格内的）
      if (columnKey === 'sceneInteriorExterior' || columnKey === 'sceneDayNight') {
        // 先尝试使用 data-scene-id 和 data-field（自定义编辑器）
        fieldElement = tableContainer.querySelector(
          `div.excel-cell-button[data-scene-id="${scene.sceneID}"][data-field="${columnKey}"]`
        ) as HTMLElement;
        
        // 如果找不到，尝试使用 data-row-id 和 data-col-key（通用方式）
        if (!fieldElement) {
          const cell = tableContainer.querySelector(
            `td[data-row-id="${scene.sceneID}"][data-col-key="${columnKey}"]`
          );
          if (cell) {
            fieldElement = cell.querySelector('div.excel-cell-button') as HTMLElement;
          }
        }
      } else {
        // 对于其他单元格，查找 input 或 select 元素（只查找表格内的）
        // 先尝试使用 data-scene-id 和 data-field（自定义编辑器）
        fieldElement = tableContainer.querySelector(
          `input[data-scene-id="${scene.sceneID}"][data-field="${columnKey}"], select[data-scene-id="${scene.sceneID}"][data-field="${columnKey}"]`
        ) as HTMLElement;
        
        // 如果找不到，尝试使用 data-row-id 和 data-col-key（通用方式，TableCell 渲染的输入框）
        if (!fieldElement) {
          fieldElement = tableContainer.querySelector(
            `input[data-row-id="${scene.sceneID}"][data-col-key="${columnKey}"], select[data-row-id="${scene.sceneID}"][data-col-key="${columnKey}"]`
          ) as HTMLElement;
        }
      }
    }

    if (!fieldElement) {
      console.warn(`[useSceneCellEdit] Field element not found: ${columnKey} for scene ${scene.sceneID}`);
      return;
    }

    // 获取原始值
    let originalValue: any;
    if (columnKey === 'sceneSet') {
      originalValue = scene.sceneSet || '';
    } else if (columnKey === 'sceneLocation') {
      // 移除对 sceneLocationDisplayTexts 的特殊处理，与 sceneSet 保持一致
      originalValue = scene.sceneLocation || '';
    } else if (fieldElement.tagName === 'INPUT' || fieldElement.tagName === 'TEXTAREA') {
      originalValue = (fieldElement as HTMLInputElement | HTMLTextAreaElement).value || '';
    }

    // 激活前回调：打开 InputRecommend 组件（对于 sceneSet 和 sceneLocation）
    const onBeforeActivate = () => {
      if (columnKey === 'sceneSet') {
        const currentValue = scene.sceneSet || '';
        setActiveSceneId(scene.sceneID);
        setSetSearchVisible(true);
        setSetSearchText(currentValue);
        setLocalSceneSetDisplay(prev => {
          const nextMap = new Map(prev);
          nextMap.set(scene.sceneID, currentValue);
          return nextMap;
        });
      } else if (columnKey === 'sceneLocation') {
        // 移除对 sceneLocationDisplayTexts 的特殊处理，与 sceneSet 保持一致
        const currentValue = scene.sceneLocation || '';
        setActiveLocationSceneId(scene.sceneID);
        setSceneLocationSearchVisible(true);
        setSceneLocationSearchText(currentValue);
        setLocalSceneLocationDisplay(prev => {
          const nextMap = new Map(prev);
          nextMap.set(scene.sceneID, currentValue);
          return nextMap;
        });
      }
    };

    // 激活后回调：计算 InputRecommend 组件位置（对于 sceneSet 和 sceneLocation）
    const onAfterActivate = () => {
      if (fieldElement instanceof HTMLInputElement) {
        if (columnKey === 'sceneSet') {
          // 使用 requestAnimationFrame 确保在聚焦完成后再计算位置，避免干扰输入法
          requestAnimationFrame(() => {
            const tableContainer = document.querySelector('.breakdown-table-container') as HTMLElement;
            if (tableContainer && fieldElement) {
              const inputRect = fieldElement.getBoundingClientRect();
              const containerRect = tableContainer.getBoundingClientRect();
              
              setSetSearchPosition({
                top: inputRect.bottom - containerRect.top + 4,
                left: inputRect.left - containerRect.left,
                width: inputRect.width
              });
            }
          });
        } else if (columnKey === 'sceneLocation') {
          // 使用 requestAnimationFrame 确保在聚焦完成后再计算位置，避免干扰输入法
          requestAnimationFrame(() => {
            const tableContainer = document.querySelector('.breakdown-table-container') as HTMLElement;
            if (tableContainer && fieldElement) {
              const inputRect = fieldElement.getBoundingClientRect();
              const containerRect = tableContainer.getBoundingClientRect();
              const componentWidth = inputRect.width;
              
              setSceneLocationSearchPosition({
                top: inputRect.bottom - containerRect.top + 4,
                left: inputRect.right - containerRect.left - componentWidth,
                width: componentWidth
              });
            }
          });
        }
      }
    };

    // 使用统一的激活方法
    // 表格模式：跳过 setFieldState，不依赖旧的快捷键组件
    activateFieldEdit({
      element: fieldElement,
      originalValue,
      onBeforeActivate: (columnKey === 'sceneSet' || columnKey === 'sceneLocation') ? onBeforeActivate : undefined,
      onAfterActivate: (columnKey === 'sceneSet' || columnKey === 'sceneLocation') ? onAfterActivate : undefined,
      autoFocus: true,
      selectText: false, // Breakdown 表格不需要全选文本
    });
  }, [
    scenes,
    setActiveSceneId,
    setSetSearchVisible,
    setSetSearchText,
    setLocalSceneSetDisplay,
    setActiveLocationSceneId,
    setSceneLocationSearchVisible,
    setSceneLocationSearchText,
    setLocalSceneLocationDisplay,
    setSetSearchPosition,
    setSceneLocationSearchPosition,
    activateFieldEdit,
  ]);

  // 完成单元格编辑
  // 注意：此函数不再调用 flushSceneUpdate，因为 onCellEditEnd 会在之后统一调用
  const finishCellEdit = useCallback((rowIndex: number, columnKey: string, shouldBlur: boolean = false) => {
    const scene = scenes[rowIndex];
    if (!scene) return;

    // 查找字段元素（只查找表格内的元素，避免找到详情页的元素）
    let fieldElement: HTMLElement | null = null;
    
    // 先查找表格容器
    const tableContainer = document.querySelector('.breakdown-table-container');
    
    if (tableContainer) {
      if (columnKey === 'sceneInteriorExterior' || columnKey === 'sceneDayNight') {
        // 先尝试使用 data-scene-id 和 data-field（自定义编辑器）
        fieldElement = tableContainer.querySelector(
          `div.excel-cell-button[data-scene-id="${scene.sceneID}"][data-field="${columnKey}"]`
        ) as HTMLElement;
        
        // 如果找不到，尝试使用 data-row-id 和 data-col-key（通用方式）
        if (!fieldElement) {
          const cell = tableContainer.querySelector(
            `td[data-row-id="${scene.sceneID}"][data-col-key="${columnKey}"]`
          );
          if (cell) {
            fieldElement = cell.querySelector('div.excel-cell-button') as HTMLElement;
          }
        }
      } else {
        // 对于其他单元格，查找 input 或 select 元素（只查找表格内的）
        // 先尝试使用 data-scene-id 和 data-field（自定义编辑器）
        fieldElement = tableContainer.querySelector(
          `input[data-scene-id="${scene.sceneID}"][data-field="${columnKey}"], select[data-scene-id="${scene.sceneID}"][data-field="${columnKey}"]`
        ) as HTMLElement;
        
        // 如果找不到，尝试使用 data-row-id 和 data-col-key（通用方式，TableCell 渲染的输入框）
        if (!fieldElement) {
          fieldElement = tableContainer.querySelector(
            `input[data-row-id="${scene.sceneID}"][data-col-key="${columnKey}"], select[data-row-id="${scene.sceneID}"][data-col-key="${columnKey}"]`
          ) as HTMLElement;
        }
      }
    }

    // 使用统一的完成方法
    // 表格模式：跳过 setFieldState，不依赖旧的快捷键组件
    // 注意：onFinish 不再调用 flushSceneUpdate，因为 onCellEditEnd 会在之后统一调用
    finishFieldEdit({
      element: fieldElement || undefined,
      onFinish: () => {
        // 不再在这里调用 flushSceneUpdate，由 onCellEditEnd 统一处理
        // 这样可以确保在 DOM 更新之前完成编辑状态清理
      },
      shouldBlur,
    });
  }, [scenes, finishFieldEdit]);

  return {
    activateCellEdit,
    finishCellEdit,
  };
};

